import { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, Grid, Hidden, Stack } from '@mui/material'
import FormSkeletonWrapper from '../../../SkeletonUI/FormSkeletonWrapper/FormSkeletonWrapper'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import MiscellaneousForm from './MiscellaneousForm'
import { toast } from 'react-toastify';
import { parseErrors, replaceNullValues } from '../../../../services/utils'
import PageTitle from '../../../Misc/PageTitle';
import MerchantMenu from '../MerchantMenu'
import MerchantAlias from '../MerchantAlias'
import MerchantSubmenu from '../MerchantSubmenu'
import useIsMounted from '../../../../hooks/useIsMounted'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import { MISCELLANEOUS__UPDATE } from '../../../../enums/Caps'
import useHasPermissions from '../../../../hooks/useHasPermissions'


export default function MiscellaneousEdit() {
    const { t } = useTranslation()

    const hasPermissions = useHasPermissions()

    const navigate = useNavigate()

    const isMounted = useIsMounted()

    const api = useAxiosPrivate()

    const [data, setData] = useState()

    const { userId, miscId } = useParams()

    const [isLoading, setLoading] = useState(true)

    const getMiscellaneous = useCallback(() => {
        api.get(`/internal/merchants/${userId}/miscellaneous/${miscId}/`)
            .then(({ data: { data } }) => isMounted() && setData(replaceNullValues(data)))
            .catch(() => isMounted() && toast.error(t('something went wrong!')))
            .finally(() => setLoading(false))
    }, [miscId, userId, t, isMounted, api])

    const handleSubmit = (values, { setFieldError }) => {
        api.put(`/internal/merchants/${userId}/miscellaneous/${miscId}/`, values)
            .then(() => isMounted() && navigate(`/merchants/${userId}/miscellaneous`))
            .catch(({ response }) => {
                if (isMounted()) {
                    const errors = parseErrors(response)

                    if (typeof errors === 'object') {
                        for (let i in errors) {
                            setFieldError(errors[i].field, t(errors[i].message), false)
                        }
                    } else if (typeof errors === 'string') {
                        toast.error(t(errors))
                    } else {
                        toast.error(t('Something went wrong'))
                    }
                }
            })
    }

    const handleCancel = () => {
        navigate(`/merchants/${userId}/miscellaneous`)
    }

    const FORM_VALIDATION = Yup.object().shape({
        currency: Yup.string()
            .required(t('This field is required')),
        comments: Yup.string()
            .required(t('This field is required')),
        type: Yup.number()
            .required(t('This field is required')),
        amount: Yup.number()
            .min(0, t('Amount field must be greater than 0'))
            .required(t('This field is required')),
    })

    useEffect(() => {
        const init = () => {
            getMiscellaneous()
        }

        init()
    }, [getMiscellaneous])

    return (
        <>
            <Card>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <PageTitle title={t('Edit miscellaneous')} />

                        <Hidden xlUp={true}>
                            {userId ? <MerchantMenu id={userId} /> : ''}
                        </Hidden>
                    </Stack>

                    <MerchantAlias />

                    <Grid container spacing={2}>
                        <Grid item xs={12} xl={2}>
                            <MerchantSubmenu current='miscellaneous' id={userId} />
                        </Grid>
                        <Grid item xs={12} xl={10}>
                            {
                                isLoading ? <FormSkeletonWrapper /> :
                                    <MiscellaneousForm
                                        isNew={false}
                                        handleSubmit={handleSubmit}
                                        handleCancel={handleCancel}
                                        initialFormState={data}
                                        FORM_VALIDATION={FORM_VALIDATION}
                                        merchant_id={userId}
                                        hasPermissions={hasPermissions(MISCELLANEOUS__UPDATE)}
                                    />
                            }
                        </Grid>
                    </Grid>

                </CardContent>
            </Card>
        </>
    )
}
